// Sport / dépense calorique — agrégation des séances et statistiques.
// NB : les calories brûlées sont un exercice PONCTUEL, additionnel au TDEE (qui
// inclut déjà l'activité de base) ; à ne pas confondre avec la maintenance.

import { aggregateBy } from './aggregate';
import { dayKey, todayKey } from './datetime';
import { mean, round } from './statistics';
import type { Activity, Exercise } from './types';

export const SPORT_ORDER: Activity[] = [
  'running',
  'strength',
  'cycling',
  'walking',
  'swimming',
  'other',
];

export const SPORT_LABELS: Record<Activity, string> = {
  running: 'Course',
  strength: 'Musculation',
  cycling: 'Vélo',
  walking: 'Marche',
  swimming: 'Natation',
  other: 'Autre',
};

export function dailyBurned(entries: readonly Exercise[]): { date: string; kcal: number }[] {
  return aggregateBy(
    entries,
    (e) => e.datetime,
    'day',
    (group) => group.reduce((sum, e) => sum + e.kcalBurned, 0),
  ).map((b) => ({ date: b.start, kcal: b.value }));
}

export interface ExerciseStats {
  today: number;
  sessions: number;
  daysLogged: number;
  avgKcalPerDay: number;
  totalKcal: number;
  currentStreak: number;
}

export function exerciseStats(
  entries: readonly Exercise[],
  today: string = todayKey(),
): ExerciseStats {
  const daily = dailyBurned(entries);
  // Clé du jour précédant `iso` (date-only).
  const prevDay = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  // Série en cours = jours calendaires consécutifs jusqu'à aujourd'hui. Rompue si
  // la dernière séance date d'avant hier (sinon un vieux bloc resterait « en cours »).
  const loggedDays = new Set(daily.map((d) => d.date));
  let current = 0;
  const last = daily[daily.length - 1]?.date;
  if (last && (last === today || last === prevDay(today))) {
    let cursor = last;
    while (loggedDays.has(cursor)) {
      current++;
      cursor = prevDay(cursor);
    }
  }
  return {
    today: daily.find((d) => d.date === today)?.kcal ?? 0,
    sessions: entries.length,
    daysLogged: daily.length,
    avgKcalPerDay: daily.length ? Math.round(mean(daily.map((d) => d.kcal))) : 0,
    totalKcal: round(daily.reduce((sum, d) => sum + d.kcal, 0)),
    currentStreak: current,
  };
}

// Une séance a-t-elle été enregistrée ce jour ? Basé sur la PRÉSENCE d'une entrée
// (pas les kcal) : une séance de muscu à 0 kcal compte tout autant.
export function hasLoggedSessionOn(
  entries: readonly Exercise[],
  day: string = todayKey(),
): boolean {
  return entries.some((e) => dayKey(e.datetime) === day);
}
